import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Search, Loader2, Plus, Filter, Calendar, MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const NewCompanies = ({ setAuth }) => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState('');
  const [sicCode, setSicCode] = useState('');
  const [category, setCategory] = useState('all');
  const [companyStatus, setCompanyStatus] = useState('all');
  const [companyType, setCompanyType] = useState('all');
  const [addingClientId, setAddingClientId] = useState(null);

  // Common business categories mapped to SIC codes
  const categoryToSicCodes = {
    'technology': '62012,62020,62090,63110', // Software, IT consulting, Web development
    'retail': '47110,47190,47710,47730,47910', // Retail trade
    'construction': '41100,41201,42110,42210,43999', // Construction activities
    'food-beverage': '56101,56103,56210,56302', // Restaurants, catering
    'healthcare': '86101,86210,86220,86900', // Medical, dental, health services
    'finance': '64191,64209,64301,64920,66190', // Banking, insurance, financial services
    'marketing': '73110,73120,73200', // Advertising, market research, PR
    'education': '85100,85200,85310,85320,85590', // Education services
    'real-estate': '68100,68201,68209,68310,68320', // Real estate activities
    'professional': '69101,69201,70221,71111', // Legal, accounting, consultancy
    'manufacturing': '10110,13100,25110,28110', // Manufacturing activities
    'logistics': '49410,52101,52102,52240', // Transport and storage
  };

  useEffect(() => {
    searchNewCompanies();
  }, []);

  const handleCategoryChange = (value) => {
    setCategory(value);
    if (value !== 'all' && categoryToSicCodes[value]) {
      setSicCode(categoryToSicCodes[value]);
    } else if (value === 'all') {
      setSicCode('');
    }
  };

  const searchNewCompanies = async () => {
    setLoading(true);
    
    // Calculate 30 days ago
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const fromDate = thirtyDaysAgo.toISOString().split('T')[0];
    const toDate = new Date().toISOString().split('T')[0];

    try {
      const response = await api.post('/companies-house/advanced-search', {
        incorporated_from: fromDate,
        incorporated_to: toDate,
        location: location || undefined,
        sic_codes: sicCode || undefined,
        company_status: companyStatus !== 'all' ? companyStatus : undefined,
        company_type: companyType !== 'all' ? companyType : undefined,
        size: 100
      });
      setCompanies(response.data.items || []);
      
      if (response.data.items.length === 0) {
        toast.info('No companies found matching your criteria');
      } else {
        const message = response.data.is_limited 
          ? `Showing ${response.data.items.length} of ${response.data.total} companies (Free tier: ${response.data.max_results} max)`
          : `Found ${response.data.items.length} companies`;
        toast.success(message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to search companies');
    } finally {
      setLoading(false);
    }
  };

  const addToClients = async (company) => {
    setAddingClientId(company.company_number);
    try {
      await api.post('/clients', {
        company_name: company.company_name,
        company_number: company.company_number
      });
      toast.success(`${company.company_name} added to clients!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add client');
    } finally {
      setAddingClientId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="border-slate-300"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Newly Formed Companies</h1>
                <p className="text-sm text-slate-500">Companies registered in the last 30 days</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6 shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-blue-600" />
              <span>Search Filters</span>
            </CardTitle>
            <CardDescription>Filter newly formed companies by criteria</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="e.g., London, Manchester"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="border-slate-300"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Business Category</Label>
                <Select value={category} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="technology">Technology & IT</SelectItem>
                    <SelectItem value="retail">Retail & E-commerce</SelectItem>
                    <SelectItem value="construction">Construction</SelectItem>
                    <SelectItem value="food-beverage">Food & Beverage</SelectItem>
                    <SelectItem value="healthcare">Healthcare & Medical</SelectItem>
                    <SelectItem value="finance">Finance & Insurance</SelectItem>
                    <SelectItem value="marketing">Marketing & Advertising</SelectItem>
                    <SelectItem value="education">Education & Training</SelectItem>
                    <SelectItem value="real-estate">Real Estate</SelectItem>
                    <SelectItem value="professional">Professional Services</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="logistics">Logistics & Transport</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sic-code">SIC Code (Optional)</Label>
                <Input
                  id="sic-code"
                  placeholder="e.g., 62012, 70221"
                  value={sicCode}
                  onChange={(e) => {
                    setSicCode(e.target.value);
                    setCategory('all'); // Reset category when manually entering SIC
                  }}
                  className="border-slate-300"
                />
                <p className="text-xs text-slate-500">Auto-filled by category or enter custom</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company-status">Status</Label>
                <Select value={companyStatus} onValueChange={setCompanyStatus}>
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="dissolved">Dissolved</SelectItem>
                    <SelectItem value="liquidation">Liquidation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company-type">Company Type</Label>
                <Select value={companyType} onValueChange={setCompanyType}>
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="ltd">Private Limited Company</SelectItem>
                    <SelectItem value="plc">Public Limited Company</SelectItem>
                    <SelectItem value="llp">Limited Liability Partnership</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex gap-3">
              <Button
                onClick={searchNewCompanies}
                disabled={loading}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                data-testid="search-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setLocation('');
                  setSicCode('');
                  setCategory('all');
                  setCompanyStatus('all');
                  setCompanyType('all');
                }}
                className="border-slate-300"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : companies.length === 0 ? (
          <Card className="shadow-sm border-slate-200">
            <CardContent className="py-20 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No companies found</h3>
              <p className="text-slate-500">Try adjusting your filters</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle>Search Results</CardTitle>
              <CardDescription>Found {companies.length} newly formed companies</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {companies.map((company, index) => (
                  <Card
                    key={index}
                    className="shadow-sm border-slate-200 hover:border-blue-300 transition-colors"
                    data-testid={`company-row-${index}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-semibold text-slate-800">{company.company_name}</h3>
                            <Badge variant="outline" className="text-xs flex-shrink-0">
                              #{company.company_number}
                            </Badge>
                            {company.company_status === 'active' ? (
                              <Badge className="bg-green-100 text-green-700 border-green-200 flex-shrink-0">Active</Badge>
                            ) : (
                              <Badge variant="outline" className="flex-shrink-0">{company.company_status}</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                            {company.date_of_creation && (
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span>Incorporated: {formatDate(company.date_of_creation)}</span>
                              </div>
                            )}
                            {company.registered_office_address?.locality && (
                              <div className="flex items-center space-x-1">
                                <MapPin className="w-4 h-4 text-slate-400" />
                                <span>{company.registered_office_address.locality}</span>
                              </div>
                            )}
                            {company.company_type && (
                              <div className="text-slate-500">
                                Type: {company.company_type}
                              </div>
                            )}
                          </div>
                          {company.sic_codes && company.sic_codes.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {company.sic_codes.slice(0, 3).map((code, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  SIC: {code}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => addToClients(company)}
                          disabled={addingClientId === company.company_number}
                          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto flex-shrink-0"
                          data-testid={`add-client-${index}`}
                        >
                          {addingClientId === company.company_number ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              Adding...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-1" />
                              Add to Clients
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default NewCompanies;
